import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Stack,
  Chip,
  Avatar,
  Divider,
  Link as MuiLink,
  useTheme,
  alpha
} from '@mui/material';
import {
  PointOfSale as PosIcon,
  Inventory as InventoryIcon,
  People as PeopleIcon,
  Store as StoreIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Cloud as CloudIcon,
  Support as SupportIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckIcon,
  Receipt as ReceiptIcon,
  Analytics as AnalyticsIcon,
  Devices as DevicesIcon,
  Email as EmailIcon,
  Language as WebIcon
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';

const LandingPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();

  // If user is already logged in, redirect to dashboard
  React.useEffect(() => {
    if (user) {
      navigate(user.isSuperAdmin ? '/super-admin' : '/app/dashboard');
    }
  }, [user, navigate]);

  const features = [
    {
      icon: <PosIcon sx={{ fontSize: 40 }} />,
      title: 'Intuitive Sales Interface',
      description: 'Process sales quickly with our user-friendly POS interface. Support for cash, card, and mobile payments.',
      color: theme.palette.primary.main
    },
    {
      icon: <InventoryIcon sx={{ fontSize: 40 }} />,
      title: 'Smart Inventory Management',
      description: 'Track stock levels in real-time, set reorder points, and receive alerts for low stock and expiring items.',
      color: theme.palette.success.main
    },
    {
      icon: <StoreIcon sx={{ fontSize: 40 }} />,
      title: 'Multi-Branch Support',
      description: 'Manage multiple store locations from a single dashboard. Track performance across all branches.',
      color: theme.palette.info.main
    },
    {
      icon: <PeopleIcon sx={{ fontSize: 40 }} />,
      title: 'Team Management',
      description: 'Assign roles and permissions to your team. Track sales by employee and manage access levels.',
      color: theme.palette.secondary.main
    },
    {
      icon: <AnalyticsIcon sx={{ fontSize: 40 }} />,
      title: 'Powerful Analytics',
      description: 'Gain insights with detailed reports on sales, inventory, and customer trends.',
      color: theme.palette.warning.main
    },
    {
      icon: <ReceiptIcon sx={{ fontSize: 40 }} />,
      title: 'Digital Receipts',
      description: 'Send receipts via email instantly. Reduce paper waste and improve customer experience.',
      color: theme.palette.error.main
    }
  ];

  const benefits = [
    'Real-time sales tracking and reporting',
    'Automated inventory alerts and management',
    'Secure cloud-based data storage',
    'Multi-device access (desktop, tablet, mobile)',
    '24/7 customer support',
    'Regular updates and new features'
  ];

  const articles = [
    {
      slug: 'pos-system-transform-business',
      title: '10 Ways a POS System Can Transform Your Business',
      excerpt: 'Discover how modern point-of-sale systems can streamline operations, reduce errors, and boost your bottom line. From inventory management to customer insights, learn the key benefits.',
      readTime: '5 min read',
      category: 'Business Growth'
    },
    {
      slug: 'efficient-inventory-management',
      title: 'The Complete Guide to Efficient Inventory Management',
      excerpt: 'Learn proven strategies for managing inventory effectively. Reduce waste, prevent stockouts, and optimize your supply chain with these expert tips.',
      readTime: '7 min read',
      category: 'Operations'
    },
    {
      slug: 'data-driven-sales-decisions',
      title: 'How to Increase Sales with Data-Driven Decisions',
      excerpt: 'Unlock the power of your sales data. Understand customer buying patterns, identify top-performing products, and make informed decisions that drive revenue.',
      readTime: '6 min read',
      category: 'Analytics'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Flex PoS - Modern Point of Sale System for Your Business</title>
        <meta name="description" content="Flex PoS is a powerful, cloud-based point of sale system designed to help businesses manage sales, inventory, and teams efficiently. Start your free trial today." />
        <meta name="keywords" content="POS system, point of sale, inventory management, sales tracking, business software, retail POS, restaurant POS" />
        <meta property="og:title" content="Flex PoS - Modern Point of Sale System" />
        <meta property="og:description" content="Streamline your business operations with Flex PoS. Manage sales, inventory, and teams from anywhere." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Flex PoS - Modern Point of Sale System" />
        <meta name="twitter:description" content="Streamline your business operations with Flex PoS." />
        <link rel="canonical" href="https://pos.kaziflex.com" />
      </Helmet>

      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        {/* Navigation */}
        <Box
          component="nav"
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1100,
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
            backdropFilter: 'blur(8px)',
            backgroundColor: alpha(theme.palette.background.paper, 0.95)
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2 }}>
              {/* Logo */}
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2.5,
                    background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1.5,
                    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                  }}
                >
                  <PosIcon sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 800,
                      letterSpacing: '-0.02em',
                      background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    Flex PoS
                  </Typography>
                </Box>
              </Box>

              {/* Nav Links */}
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  component={Link}
                  to="/login"
                  sx={{ fontWeight: 600 }}
                >
                  Sign In
                </Button>
                <Button
                  component={Link}
                  to="/register"
                  variant="contained"
                  sx={{
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
                    }
                  }}
                >
                  Get Started
                </Button>
              </Stack>
            </Box>
          </Container>
        </Box>

        {/* Hero Section */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 50%, #EC4899 100%)',
            color: 'white',
            py: { xs: 8, md: 12 },
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Background Pattern */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              opacity: 0.1,
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />

          <Container maxWidth="lg" sx={{ position: 'relative' }}>
            <Grid container spacing={6} alignItems="center">
              <Grid size={{ xs: 12, md: 6 }}>
                <Chip
                  label="Modern POS Solution"
                  sx={{
                    mb: 3,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontWeight: 600,
                    backdropFilter: 'blur(4px)'
                  }}
                />
                <Typography
                  variant="h1"
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    lineHeight: 1.1,
                    mb: 3,
                    letterSpacing: '-0.02em'
                  }}
                >
                  Run Your Business{' '}
                  <Box component="span" sx={{ color: alpha('#fff', 0.9) }}>
                    Smarter
                  </Box>
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    mb: 4,
                    opacity: 0.9,
                    fontWeight: 400,
                    lineHeight: 1.6,
                    fontSize: { xs: '1.1rem', md: '1.25rem' }
                  }}
                >
                  Flex PoS is the all-in-one point of sale system that helps you manage sales,
                  inventory, and your team—all from one powerful dashboard.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Button
                    component={Link}
                    to="/register"
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      bgcolor: 'white',
                      color: 'primary.main',
                      fontWeight: 700,
                      px: 4,
                      py: 1.5,
                      fontSize: '1rem',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.9)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 20px rgba(0,0,0,0.2)'
                      }
                    }}
                  >
                    Start Free Trial
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    sx={{
                      borderColor: 'rgba(255,255,255,0.5)',
                      color: 'white',
                      fontWeight: 600,
                      px: 4,
                      py: 1.5,
                      '&:hover': {
                        borderColor: 'white',
                        bgcolor: 'rgba(255,255,255,0.1)'
                      }
                    }}
                    onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
                  >
                    Learn More
                  </Button>
                </Stack>

                {/* Trust Indicators */}
                <Box sx={{ mt: 5 }}>
                  <Stack direction="row" spacing={4} flexWrap="wrap">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SecurityIcon sx={{ opacity: 0.8 }} />
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>Secure & Encrypted</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CloudIcon sx={{ opacity: 0.8 }} />
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>Cloud-Based</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SupportIcon sx={{ opacity: 0.8 }} />
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>24/7 Support</Typography>
                    </Box>
                  </Stack>
                </Box>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                {/* Hero Visual - Floating Feature Cards */}
                <Box
                  sx={{
                    position: 'relative',
                    height: { xs: 300, md: 400 },
                    display: { xs: 'none', md: 'block' }
                  }}
                >
                  {/* Main Dashboard Card */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '10%',
                      left: '10%',
                      bgcolor: 'white',
                      borderRadius: 3,
                      p: 2.5,
                      boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
                      width: 220,
                      transform: 'rotate(-3deg)',
                      zIndex: 3
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#10B981' }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        Sales Dashboard
                      </Typography>
                    </Box>
                    <Typography variant="h5" color="text.primary" fontWeight={700} sx={{ mb: 0.5 }}>
                      KES 128,450
                    </Typography>
                    <Typography variant="caption" color="success.main" fontWeight={600}>
                      +12.5% from last week
                    </Typography>
                    <Box sx={{ mt: 2, display: 'flex', gap: 0.5 }}>
                      {[40, 65, 45, 80, 55, 90, 70].map((h, i) => (
                        <Box
                          key={i}
                          sx={{
                            width: 20,
                            height: h * 0.6,
                            bgcolor: alpha(theme.palette.primary.main, 0.2 + (i * 0.1)),
                            borderRadius: 1
                          }}
                        />
                      ))}
                    </Box>
                  </Box>

                  {/* Inventory Card */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '35%',
                      right: '5%',
                      bgcolor: 'white',
                      borderRadius: 3,
                      p: 2,
                      boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
                      width: 180,
                      transform: 'rotate(2deg)',
                      zIndex: 2
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <InventoryIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        Inventory
                      </Typography>
                    </Box>
                    <Stack spacing={1}>
                      {[
                        { name: 'In Stock', value: 1247, color: 'success.main' },
                        { name: 'Low Stock', value: 23, color: 'warning.main' },
                      ].map((item, i) => (
                        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="caption" color="text.secondary">{item.name}</Typography>
                          <Chip
                            label={item.value}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              bgcolor: alpha(theme.palette[item.color.split('.')[0]][item.color.split('.')[1]], 0.1),
                              color: item.color
                            }}
                          />
                        </Box>
                      ))}
                    </Stack>
                  </Box>

                  {/* Quick Sale Card */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: '5%',
                      left: '20%',
                      bgcolor: 'white',
                      borderRadius: 3,
                      p: 2,
                      boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
                      width: 200,
                      transform: 'rotate(-1deg)',
                      zIndex: 4
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <ReceiptIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        Recent Transaction
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="body2" color="text.primary" fontWeight={600}>
                          Order #2847
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          3 items • Just now
                        </Typography>
                      </Box>
                      <Typography variant="subtitle2" color="success.main" fontWeight={700}>
                        KES 2,450
                      </Typography>
                    </Box>
                  </Box>

                  {/* Decorative Elements */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '5%',
                      right: '20%',
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      bgcolor: 'rgba(255,255,255,0.1)',
                      border: '2px solid rgba(255,255,255,0.2)'
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: '20%',
                      right: '10%',
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      bgcolor: 'rgba(255,255,255,0.1)',
                      transform: 'rotate(15deg)'
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Features Section */}
        <Box id="features" sx={{ py: { xs: 8, md: 12 }, bgcolor: 'background.paper' }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Chip
                label="Features"
                sx={{ mb: 2, fontWeight: 600 }}
                color="primary"
                variant="outlined"
              />
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: '2rem', md: '2.5rem' }
                }}
              >
                Everything You Need to Succeed
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ maxWidth: 600, mx: 'auto', fontWeight: 400 }}
              >
                Powerful tools designed to help you manage every aspect of your business efficiently.
              </Typography>
            </Box>

            <Grid container spacing={4}>
              {features.map((feature, index) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                  <Card
                    sx={{
                      height: '100%',
                      p: 3,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 12px 24px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: 3,
                        bgcolor: alpha(feature.color, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                        color: feature.color
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" lineHeight={1.7}>
                      {feature.description}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Benefits Section */}
        <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'grey.50' }}>
          <Container maxWidth="lg">
            <Grid container spacing={6} alignItems="center">
              <Grid size={{ xs: 12, md: 6 }}>
                <Chip
                  label="Why Choose Us"
                  sx={{ mb: 2, fontWeight: 600 }}
                  color="primary"
                  variant="outlined"
                />
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 700,
                    mb: 3,
                    fontSize: { xs: '2rem', md: '2.5rem' }
                  }}
                >
                  Built for Businesses Like Yours
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.8 }}>
                  Whether you run a retail store, restaurant, pharmacy, or service business,
                  Flex PoS adapts to your unique needs. Our system is designed to grow with you.
                </Typography>

                <Stack spacing={2}>
                  {benefits.map((benefit, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <CheckIcon sx={{ color: 'success.main' }} />
                      <Typography variant="body1">{benefit}</Typography>
                    </Box>
                  ))}
                </Stack>

                <Button
                  component={Link}
                  to="/register"
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  sx={{ mt: 4, fontWeight: 600 }}
                >
                  Get Started Today
                </Button>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Box
                  sx={{
                    bgcolor: 'white',
                    borderRadius: 4,
                    p: 4,
                    boxShadow: '0 20px 40px rgba(0,0,0,0.08)'
                  }}
                >
                  <Stack spacing={3}>
                    {[
                      { icon: <SpeedIcon />, title: 'Lightning Fast', desc: 'Process transactions in seconds' },
                      { icon: <DevicesIcon />, title: 'Works Everywhere', desc: 'Desktop, tablet, or mobile' },
                      { icon: <SecurityIcon />, title: 'Bank-Level Security', desc: 'Your data is always protected' }
                    ].map((item, index) => (
                      <Box key={index} sx={{ display: 'flex', gap: 3 }}>
                        <Avatar
                          sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: 'primary.main'
                          }}
                        >
                          {item.icon}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600}>{item.title}</Typography>
                          <Typography variant="body2" color="text.secondary">{item.desc}</Typography>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Articles Section */}
        <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'background.paper' }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Chip
                label="Resources"
                sx={{ mb: 2, fontWeight: 600 }}
                color="primary"
                variant="outlined"
              />
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: '2rem', md: '2.5rem' }
                }}
              >
                Tips for Business Success
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ maxWidth: 600, mx: 'auto', fontWeight: 400 }}
              >
                Learn from industry experts and discover strategies to grow your business.
              </Typography>
            </Box>

            <Grid container spacing={4}>
              {articles.map((article, index) => (
                <Grid size={{ xs: 12, md: 4 }} key={index}>
                  <Card
                    onClick={() => navigate(`/articles/${article.slug}`)}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 12px 24px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    <Box
                      sx={{
                        height: 160,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.8)} 0%, ${alpha(theme.palette.secondary.main, 0.8)} 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <AnalyticsIcon sx={{ fontSize: 48, color: 'white', opacity: 0.9 }} />
                    </Box>
                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                        <Chip label={article.category} size="small" color="primary" variant="outlined" />
                        <Chip label={article.readTime} size="small" variant="outlined" />
                      </Stack>
                      <Typography variant="h6" fontWeight={600} gutterBottom sx={{ lineHeight: 1.3 }}>
                        {article.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                        {article.excerpt}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* CTA Section */}
        <Box
          sx={{
            py: { xs: 8, md: 10 },
            background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
            color: 'white',
            textAlign: 'center'
          }}
        >
          <Container maxWidth="md">
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: '1.75rem', md: '2.5rem' }
              }}
            >
              Ready to Transform Your Business?
            </Typography>
            <Typography
              variant="h6"
              sx={{ mb: 4, opacity: 0.9, fontWeight: 400 }}
            >
              Join thousands of businesses already using Flex PoS to streamline their operations.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button
                component={Link}
                to="/register"
                variant="contained"
                size="large"
                sx={{
                  bgcolor: 'white',
                  color: 'primary.main',
                  fontWeight: 700,
                  px: 5,
                  py: 1.5,
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.9)'
                  }
                }}
              >
                Start Free Trial
              </Button>
              <Button
                component={Link}
                to="/contact"
                variant="outlined"
                size="large"
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  fontWeight: 600,
                  px: 5,
                  py: 1.5,
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)'
                  }
                }}
              >
                Contact Sales
              </Button>
            </Stack>
          </Container>
        </Box>

        {/* Footer */}
        <Box
          component="footer"
          sx={{
            bgcolor: '#0F172A',
            color: 'white',
            py: 8
          }}
        >
          <Container maxWidth="lg">
            <Grid container spacing={6}>
              {/* Company Info */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2.5,
                      background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 1.5
                    }}
                  >
                    <PosIcon sx={{ color: 'white', fontSize: 24 }} />
                  </Box>
                  <Typography variant="h6" fontWeight={700}>
                    Flex PoS
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'grey.400', lineHeight: 1.8 }}>
                  Flex PoS is a modern point of sale system designed to help businesses of all sizes
                  manage sales, inventory, and teams efficiently.
                </Typography>
              </Grid>

              {/* Quick Links */}
              <Grid size={{ xs: 6, md: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Product
                </Typography>
                <Stack spacing={1.5}>
                  <MuiLink
                    href="#features"
                    sx={{
                      color: 'grey.400',
                      textDecoration: 'none',
                      '&:hover': { color: 'white' }
                    }}
                  >
                    Features
                  </MuiLink>
                  <MuiLink
                    component={Link}
                    to="/register"
                    sx={{
                      color: 'grey.400',
                      textDecoration: 'none',
                      '&:hover': { color: 'white' }
                    }}
                  >
                    Get Started
                  </MuiLink>
                </Stack>
              </Grid>

              <Grid size={{ xs: 6, md: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Resources
                </Typography>
                <Stack spacing={1.5}>
                  <MuiLink
                    component={Link}
                    to="/articles/pos-system-transform-business"
                    sx={{
                      color: 'grey.400',
                      textDecoration: 'none',
                      '&:hover': { color: 'white' }
                    }}
                  >
                    POS Guide
                  </MuiLink>
                  <MuiLink
                    component={Link}
                    to="/articles/efficient-inventory-management"
                    sx={{
                      color: 'grey.400',
                      textDecoration: 'none',
                      '&:hover': { color: 'white' }
                    }}
                  >
                    Inventory Tips
                  </MuiLink>
                </Stack>
              </Grid>

              <Grid size={{ xs: 6, md: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Support
                </Typography>
                <Stack spacing={1.5}>
                  <MuiLink component={Link} to="/contact" sx={{ color: 'grey.400', textDecoration: 'none', '&:hover': { color: 'white' } }}>
                    Contact Us
                  </MuiLink>
                  <MuiLink component={Link} to="/terms" sx={{ color: 'grey.400', textDecoration: 'none', '&:hover': { color: 'white' } }}>
                    Terms of Service
                  </MuiLink>
                  <MuiLink component={Link} to="/terms?tab=privacy" sx={{ color: 'grey.400', textDecoration: 'none', '&:hover': { color: 'white' } }}>
                    Privacy Policy
                  </MuiLink>
                </Stack>
              </Grid>

              {/* Contact */}
              <Grid size={{ xs: 6, md: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Contact
                </Typography>
                <Stack spacing={1.5}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'grey.400' }}>
                    <EmailIcon sx={{ fontSize: 18 }} />
                    <MuiLink
                      href="mailto:support@kaziflex.com"
                      sx={{ color: 'grey.400', textDecoration: 'none', '&:hover': { color: 'white' } }}
                    >
                      support@kaziflex.com
                    </MuiLink>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'grey.400' }}>
                    <WebIcon sx={{ fontSize: 18 }} />
                    <MuiLink
                      href="https://www.kaziflex.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: 'grey.400', textDecoration: 'none', '&:hover': { color: 'white' } }}
                    >
                      www.kaziflex.com
                    </MuiLink>
                  </Box>
                </Stack>
              </Grid>
            </Grid>

            <Divider sx={{ borderColor: 'grey.800', my: 4 }} />

            {/* Bottom Footer */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2
              }}
            >
              <Typography variant="body2" sx={{ color: 'grey.500' }}>
                © {new Date().getFullYear()} Flex PoS. All rights reserved.
              </Typography>
              <Typography variant="body2" sx={{ color: 'grey.500' }}>
                Developed by{' '}
                <MuiLink
                  href="https://www.kaziflex.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: 'primary.light', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                  Kazi Flex LLC
                </MuiLink>
              </Typography>
            </Box>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default LandingPage;
